// The field-level diff between two releases (spec/dashboard.md section 12.2): `GET …/compare?from=<the earlier release's
// commit>&to=<the later one's>&include=content`, rendered with the history's changes grid (leaf diffs with links to the
// entities). The build dialog's deploy diff, Promote… and Pin… all show it, so a release is reviewed as documents and
// fields, never as file paths alone.
import { ops } from '../../api/operations';
import type { CommitDetail } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { useQuery } from '../../workspace/cache';
import { asApiError } from './releases';

export interface ReleaseDiffProps {
  /** the branch whose compare endpoint answers (the repository is per game, so any two commits of the game resolve) */
  branch: string;
  /** the earlier release's commit; null when there is none (a first release) */
  fromCommit: string | null;
  toCommit: string;
  emptyMessage?: string;
}

export function ReleaseDiff({ branch, fromCommit, toCommit, emptyMessage = 'The two releases hold the same content.' }: ReleaseDiffProps) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const key = fromCommit && fromCommit !== toCommit ? `game:${game.id}:release-diff:${fromCommit}:${toCommit}` : null;
  const q = useQuery(key, () => client.data(ops.compareCommits, { path: { org, game: game.slug, branch }, query: { from: fromCommit!, to: toCommit, include: 'content' } }) as unknown as Promise<CommitDetail>);
  if (!fromCommit) return <p className="muted small">No earlier release to compare with: every document is new.</p>;
  if (fromCommit === toCommit) return <p className="muted small">{emptyMessage}</p>;
  if (q.error) return <LoadError>{asApiError(q.error).message}</LoadError>;
  if (!q.data) return <LoadingPane rows={3} label="Comparing the releases" />;
  return <ChangesGrid changes={q.data.changes as ContentChange[]} branch={branch} contentTruncated={q.data.contentTruncated ?? undefined} emptyMessage={emptyMessage} />;
}
